import React, { useState, useMemo, useCallback } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Spinner,
  Input,
  Tooltip,
} from '@material-tailwind/react';
import {
  PhotoIcon,
  MapPinIcon,
  ClockIcon,
  XMarkIcon,
  PlusIcon,
  ArrowPathIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

const formatPrice = (value, currency = 'USD') => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatHours = (value) => {
  if (!value && value !== 0) return 'N/A';
  return `${value.toLocaleString()} hrs`;
};

function CompCard({ comp, onRemove, isRemoving, isExcluded }) {
  const {
    reg,
    registration,
    year,
    model,
    totalTime,
    total_time,
    price_usd,
    source,
    photoUrl,
    location,
    relevanceNotes,
    daysOnMarket,
  } = comp;

  const displayReg = reg || registration;
  const displayHours = totalTime || total_time;
  const displayPrice = price_usd;

  return (
    <Card className={`overflow-hidden relative group ${isExcluded ? 'opacity-50 grayscale' : ''}`}>
      {/* Toggle exclusion button */}
      {onRemove && (
        <IconButton
          size="sm"
          color={isExcluded ? "green" : "red"}
          variant="filled"
          className="absolute top-1 left-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 min-w-0"
          onClick={() => onRemove(displayReg)}
          disabled={isRemoving}
        >
          {isExcluded ? <ArrowPathIcon className="h-4 w-4" /> : <XMarkIcon className="h-4 w-4" />}
        </IconButton>
      )}

      <div className="relative h-28 bg-gray-100 flex items-center justify-center">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={`${displayReg} ${model}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <PhotoIcon className="h-8 w-8 text-gray-300" />
        )}
        {source && (
          <Chip
            size="sm"
            value={source}
            className="absolute top-1 right-1 text-xs"
            color="blue-gray"
          />
        )}
      </div>
      <CardBody className="p-2">
        <div className="flex items-start justify-between mb-1">
          <div>
            <Typography variant="small" color="blue-gray" className="font-bold">
              {displayReg || 'N/A'}
            </Typography>
            <Typography variant="small" color="gray" className="text-xs">
              {year} {model}
            </Typography>
          </div>
          <div className="flex items-center gap-1">
            <Typography variant="small" color="blue" className="font-bold">
              {formatPrice(displayPrice)}
            </Typography>
            {relevanceNotes && relevanceNotes.length > 0 && (
              <Tooltip
                content={
                  <div className="p-1">
                    <Typography variant="small" className="font-medium text-white text-xs mb-1">
                      Relevance
                    </Typography>
                    <ul className="list-disc list-inside text-xs">
                      {relevanceNotes.map((note, idx) => (
                        <li key={idx}>{note}</li>
                      ))}
                    </ul>
                  </div>
                }
              >
                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 hover:text-blue-500 cursor-help" />
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1 text-xs">
          <div className="flex items-center gap-1 text-gray-600">
            <ClockIcon className="h-3 w-3" />
            <span>{formatHours(displayHours)}</span>
          </div>
          {location && (
            <div className="flex items-center gap-1 text-gray-600">
              <MapPinIcon className="h-3 w-3" />
              <span>{location}</span>
            </div>
          )}
          {daysOnMarket !== undefined && (
            <Chip
              size="sm"
              value={`${daysOnMarket} DOM`}
              variant="ghost"
              color="gray"
              className="text-xs py-0"
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
}

const EMPTY_COMP_FORM = {
  reg: '',
  year: '',
  totalTime: '',
  price_usd: '',
  source: '',
  model: '',
};

function OnMarketCompsSection({
  content,
  originalContent,
  onCompsChange,
  onResetToAI,
  excludedListings = [],
  onToggleExclusion,
  isUpdating,
  hasLocalChanges,
}) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCompForm, setNewCompForm] = useState(EMPTY_COMP_FORM);
  const [formError, setFormError] = useState('');

  // Get current comps (may be AI-selected or user-edited)
  const currentComps = useMemo(() => content?.comparables || content?.listings || [], [content?.comparables, content?.listings]);

  // Get AI-selected comps for comparison
  const aiSelectedComps = useMemo(() => originalContent?.comparables || originalContent?.listings || [], [originalContent?.comparables, originalContent?.listings]);

  // Check if user has modified the comps
  const hasUserEdits = useMemo(() => {
    if (!currentComps.length && !aiSelectedComps.length) return false;
    if (currentComps.length !== aiSelectedComps.length) return true;

    const currentRegs = currentComps.map(c => c.reg || c.registration).sort();
    const aiRegs = aiSelectedComps.map(c => c.reg || c.registration).sort();
    return JSON.stringify(currentRegs) !== JSON.stringify(aiRegs);
  }, [currentComps, aiSelectedComps]);

  const isExcluded = useCallback((reg) => {
    if (!reg) return false;
    return excludedListings.some(r => r.toUpperCase() === reg.toUpperCase());
  }, [excludedListings]);

  const handleRemove = (regToRemove) => {
    if (onToggleExclusion) {
      onToggleExclusion(regToRemove);
      return;
    }
    if (!onCompsChange) return;
    const updated = currentComps.filter(c => (c.reg || c.registration) !== regToRemove);
    onCompsChange(updated);
  };

  const handleAddComp = () => {
    if (!onCompsChange) return;

    // Validate required fields
    if (!newCompForm.reg.trim()) {
      setFormError('Registration is required');
      return;
    }
    if (!newCompForm.year) {
      setFormError('Year is required');
      return;
    }
    if (!newCompForm.price_usd) {
      setFormError('Price is required');
      return;
    }

    // Check for duplicate registration
    const existingReg = currentComps.find(
      c => (c.reg || c.registration)?.toUpperCase() === newCompForm.reg.toUpperCase()
    );
    if (existingReg) {
      setFormError('A comp with this registration already exists');
      return;
    }

    // Create new comp object
    const newComp = {
      reg: newCompForm.reg.toUpperCase(),
      year: parseInt(newCompForm.year, 10),
      price_usd: parseInt(newCompForm.price_usd, 10),
      totalTime: newCompForm.totalTime ? parseInt(newCompForm.totalTime, 10) : undefined,
      source: newCompForm.source || 'Manual Entry',
      model: newCompForm.model || undefined,
    };

    const updated = [...currentComps, newComp];
    onCompsChange(updated);

    // Reset form and close dialog
    setNewCompForm(EMPTY_COMP_FORM);
    setFormError('');
    setShowAddDialog(false);
  };

  const handleReset = () => {
    if (onResetToAI) onResetToAI();
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setNewCompForm(EMPTY_COMP_FORM);
    setFormError('');
  };

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No on-market comparables data available</Typography>
      </div>
    );
  }

  const { relevanceCriteria, selectionCriteria, summary } = content;

  return (
    <div className="space-y-3 p-2 overflow-auto h-full">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Typography variant="small" className="font-semibold">
            On-Market Comparables ({currentComps.filter(c => !isExcluded(c.reg || c.registration)).length}{currentComps.some(c => isExcluded(c.reg || c.registration)) ? ` / ${currentComps.length}` : ''})
          </Typography>
          {hasLocalChanges && (
            <Chip size="sm" value="Pending" color="amber" variant="filled" />
          )}
          {hasUserEdits && !hasLocalChanges && (
            <Chip size="sm" value="Edited" color="amber" variant="ghost" />
          )}
          {isUpdating && <Spinner className="h-4 w-4" />}
        </div>

        {(onCompsChange || onResetToAI) && (
          <div className="flex items-center gap-2">
            {(hasUserEdits || hasLocalChanges || excludedListings.length > 0) && onResetToAI && (
              <Button
                variant="text"
                size="sm"
                color="gray"
                className="flex items-center gap-1 py-1 px-2"
                onClick={handleReset}
                disabled={isUpdating}
              >
                <ArrowPathIcon className="h-3 w-3" />
                Reset to AI Selection
              </Button>
            )}
            <Button
              variant="outlined"
              size="sm"
              color="blue"
              className="flex items-center gap-1 py-1 px-2"
              onClick={() => setShowAddDialog(true)}
              disabled={isUpdating}
            >
              <PlusIcon className="h-3 w-3" />
              Add Comp
            </Button>
          </div>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
          <Typography variant="small" color="blue-gray" className="font-medium text-xs mb-0.5">
            Selection Summary
          </Typography>
          <Typography variant="small" color="gray" className="text-xs">
            {summary}
          </Typography>
        </div>
      )}

      {/* Selection Criteria */}
      {(relevanceCriteria || selectionCriteria) && (
        <Card className="bg-gray-50">
          <CardBody className="p-2">
            <Typography variant="small" className="font-medium text-xs mb-1">
              Selection Criteria
            </Typography>
            <Typography variant="small" color="gray" className="whitespace-pre-wrap text-xs">
              {relevanceCriteria || selectionCriteria}
            </Typography>
          </CardBody>
        </Card>
      )}

      {/* Comparable Listings Grid */}
      {currentComps.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {currentComps.map((comp, index) => (
            <CompCard
              key={comp.listingId || comp.reg || comp.registration || index}
              comp={comp}
              onRemove={(onToggleExclusion || onCompsChange) ? handleRemove : null}
              isRemoving={isUpdating}
              isExcluded={isExcluded(comp.reg || comp.registration)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          <Typography variant="small">No comparable listings selected</Typography>
          {onCompsChange && (
            <Button
              variant="text"
              size="sm"
              color="blue"
              className="mt-2"
              onClick={() => setShowAddDialog(true)}
            >
              Add comparables
            </Button>
          )}
        </div>
      )}

      {/* Add Comp Dialog */}
      <Dialog open={showAddDialog} handler={handleCloseDialog} size="sm">
        <DialogHeader>Add Comparable</DialogHeader>
        <DialogBody>
          {formError && (
            <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {formError}
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Registration *"
              value={newCompForm.reg}
              onChange={(e) => setNewCompForm(prev => ({ ...prev, reg: e.target.value.toUpperCase() }))}
              placeholder="e.g., N123AB"
            />

            <Input
              label="Model"
              value={newCompForm.model}
              onChange={(e) => setNewCompForm(prev => ({ ...prev, model: e.target.value }))}
              placeholder="e.g., Citation XLS+"
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                label="Year *"
                value={newCompForm.year}
                onChange={(e) => setNewCompForm(prev => ({ ...prev, year: e.target.value }))}
                placeholder="e.g., 2019"
              />

              <Input
                type="number"
                label="Total Time (hrs)"
                value={newCompForm.totalTime}
                onChange={(e) => setNewCompForm(prev => ({ ...prev, totalTime: e.target.value }))}
                placeholder="e.g., 1200"
              />
            </div>

            <Input
              type="number"
              label="Price (USD) *"
              value={newCompForm.price_usd}
              onChange={(e) => setNewCompForm(prev => ({ ...prev, price_usd: e.target.value }))}
              placeholder="e.g., 450000"
            />

            <Input
              label="Source"
              value={newCompForm.source}
              onChange={(e) => setNewCompForm(prev => ({ ...prev, source: e.target.value }))}
              placeholder="e.g., Controller, Trade-A-Plane"
            />
          </div>
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button variant="text" onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button onClick={handleAddComp} disabled={isUpdating}>
            {isUpdating ? <Spinner className="h-4 w-4" /> : 'Add Comp'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default OnMarketCompsSection;
